import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from "react";
import { CheckCircle2, AlertCircle, AlertTriangle, Info, X } from "lucide-react";
import "./Toast.css";

const ToastContext = createContext(null);

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    // Return a dummy fallback in case someone calls outside provider during tests
    return { addToast: () => {}, removeToast: () => {} };
  }
  return context;
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, typeOrOptions = "success", extraOptions = {}) => {
    let finalType = "success";
    let finalMessage = "";
    let finalTitle = "";
    let duration = 4500;

    // Handle flexible arguments:
    // 1. addToast("text", "error")
    // 2. addToast("text", { type: "error", title: "Oops" })
    // 3. addToast("text", "error", { title: "Oops", duration: 6000 })
    let opts = {};
    if (typeof typeOrOptions === "object" && typeOrOptions !== null) {
      opts = typeOrOptions;
      finalType = opts.type || "success";
    } else if (typeof typeOrOptions === "string") {
      finalType = typeOrOptions;
      if (typeof extraOptions === "object" && extraOptions !== null) {
        opts = extraOptions;
      }
    }

    if (opts.duration) duration = opts.duration;
    if (opts.title) finalTitle = opts.title;

    // Parse input message (string, JSON string, Error, or object)
    if (typeof message === "string") {
      try {
        const parsed = JSON.parse(message);
        if (parsed && typeof parsed === "object") {
          finalTitle = finalTitle || parsed.title || "";
          finalMessage = parsed.error || parsed.message || (parsed.title ? "" : message);
        } else {
          finalMessage = message;
        }
      } catch (e) {
        finalMessage = message;
      }
    } else if (message instanceof Error) {
      finalMessage = message.message || "An unexpected error occurred.";
    } else if (typeof message === "object" && message !== null) {
      finalTitle = finalTitle || message.title || "";
      finalMessage = message.error || message.message || (message.title ? "" : JSON.stringify(message));
      if (message.type) finalType = message.type;
      if (message.duration) duration = message.duration;
    } else {
      finalMessage = String(message || "");
    }

    // Default duration extension for longer messages so user has adequate reading time
    if (!opts.duration && finalMessage.length > 80) {
      duration = Math.min(7000, 4500 + (finalMessage.length - 80) * 25);
    }

    const id = Date.now().toString(36) + Math.random().toString(36).substring(2, 7);
    setToasts((prev) => [...prev, { id, title: finalTitle, message: finalMessage, type: finalType, duration }]);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ addToast, removeToast }}>
      {children}
      <div className="toast-container" aria-label="Notifications" role="region">
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onRemove={removeToast} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

function ToastItem({ toast, onRemove }) {
  const [isExiting, setIsExiting] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  const duration = toast.duration || 4500;
  const remainingTimeRef = useRef(duration);
  const startTimeRef = useRef(Date.now());
  const timerRef = useRef(null);
  const exitTimerRef = useRef(null);

  const startTimer = useCallback((time) => {
    startTimeRef.current = Date.now();
    timerRef.current = setTimeout(() => {
      setIsExiting(true);
    }, time);
  }, []);

  useEffect(() => {
    startTimer(remainingTimeRef.current);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (exitTimerRef.current) clearTimeout(exitTimerRef.current);
    };
  }, [startTimer]);

  const handleMouseEnter = () => {
    if (isExiting) return;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    const elapsed = Date.now() - startTimeRef.current;
    remainingTimeRef.current = Math.max(remainingTimeRef.current - elapsed, 1000);
    setIsPaused(true);
  };

  const handleMouseLeave = () => {
    if (isExiting) return;
    setIsPaused(false);
    startTimer(remainingTimeRef.current);
  };

  const handleClose = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setIsExiting(true);
  };

  useEffect(() => {
    if (isExiting) {
      exitTimerRef.current = setTimeout(() => {
        onRemove(toast.id);
      }, 280);
      return () => {
        if (exitTimerRef.current) clearTimeout(exitTimerRef.current);
      };
    }
  }, [isExiting, onRemove, toast.id]);

  const getIcon = () => {
    switch (toast.type) {
      case "error":
        return <AlertCircle size={18} strokeWidth={2.2} />;
      case "warning":
        return <AlertTriangle size={18} strokeWidth={2.2} />;
      case "info":
        return <Info size={18} strokeWidth={2.2} />;
      case "success":
      default:
        return <CheckCircle2 size={18} strokeWidth={2.2} />;
    }
  };

  const isRTL = typeof document !== "undefined" && document.documentElement.dir === "rtl";

  return (
    <div
      className={`toast toast--${toast.type} ${isExiting ? "toast--exit" : ""} ${isPaused ? "is-paused" : ""}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      role={toast.type === "error" || toast.type === "warning" ? "alert" : "status"}
      aria-live={toast.type === "error" ? "assertive" : "polite"}
    >
      <div className="toast__icon-badge" aria-hidden="true">
        {getIcon()}
      </div>

      <div className={`toast__content ${!toast.title ? "toast__content--single" : ""}`}>
        {toast.title && <div className="toast__title">{toast.title}</div>}
        <div className="toast__message">{toast.message}</div>
      </div>

      <button
        type="button"
        className="toast__close"
        onClick={handleClose}
        aria-label={isRTL ? "إغلاق التنبيه" : "Close notification"}
        title={isRTL ? "إغلاق" : "Close"}
      >
        <X size={15} />
      </button>

      <div className="toast__progress" aria-hidden="true">
        <div
          className="toast__progress-bar"
          style={{ animationDuration: `${duration}ms` }}
        />
      </div>
    </div>
  );
}
